import React from "react";
import { computeDistanceKm } from "./distanceUtils";
import { safeLocaleText } from "../utils/safeLocaleText";
import noSearchImg from "../../../../images/noResults.png";
import UserInitialsAvatar from "../../../../../components/UserInitialsAvatar/UserInitialsAvatar";

const PartnerB2BResultsList = ({ results = [], hasSearched, distance, onSelect, userCoords }) => {
  const hasResults = Array.isArray(results) && results.length > 0;

  const getDisplayName = (partner) => {
    if (!partner) return "-";
    return (
      partner?.storeName ||
      [partner?.user?.firstname, partner?.user?.surname].filter(Boolean).join(" ") ||
      partner?.username ||
      "-"
    );
  };

  const getDistanceLabel = (partner) => {
    if (!partner || !userCoords) return null;
    const value = computeDistanceKm(userCoords, partner);
    if (value == null || !Number.isFinite(value)) return null;
    if (value < 1) return `${(value * 1000).toFixed(0)}m`;
    if (value < 10) return `${value.toFixed(1)}km`;
    return `${value.toFixed(0)}km`;
  };

  return (
    <div
      style={{
        maxHeight: "600px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="flex align-items-center justify-content-between">
        <div
          className="w-full px-2 pb-3 pt-2"
          style={{ borderBottom: "1px solid var(--theme-border, #eaeaea)" }}
        >
          <div
            className="text-sm font-semibold"
            style={{
              color: "var(--theme-text, #171717)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {safeLocaleText("partnerB2bClosestPartners", "Closest partners")}
          </div>
          {hasResults && (
            <div
              className="text-xs"
              style={{
                color: "var(--theme-text-muted, #5C5C5C)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {safeLocaleText(
                "partnerB2bShowingResultsInRadius",
                "Showing {count} {countLabel} within {radius} km",
              )
                .replace("{count}", results.length)
                .replace(
                  "{countLabel}",
                  results.length === 1
                    ? safeLocaleText("partnerB2bResult", "result")
                    : safeLocaleText("partnerB2bResults", "results"),
                )
                .replace("{radius}", Number(distance) || 0)}
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", marginTop: "0.75rem" }}>
        {!hasSearched && (
          <div
            className="flex flex-column align-items-center justify-content-center text-center"
            style={{
              height: "100%",
              color: "var(--theme-text-muted, #5C5C5C)",
            }}
          >
            <img
              src={noSearchImg}
              alt="No search"
              className="my-3"
              style={{ width: "100px", height: "100px", objectFit: "cover" }}
            />
            <p className="m-0 text-sm" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
              {safeLocaleText(
                "partnerB2bWaitingForSearch",
                "Waiting for a search to list partners.",
              )}
            </p>
          </div>
        )}

        {hasSearched && !hasResults && (
          <div
            className="flex flex-column align-items-center justify-content-center text-center"
            style={{
              height: "100%",
              color: "var(--theme-text-muted, #5C5C5C)",
            }}
          >
            <i
              className="pi pi-info-circle"
              style={{
                fontSize: "2rem",
                marginBottom: "0.5rem",
                color: "var(--theme-primary, #1DAF61)",
              }}
            />
            <p className="m-0 text-sm">
              {safeLocaleText(
                "partnerB2bNoPartnersFound",
                "No partners found for the current filters.",
              )}
            </p>
          </div>
        )}

        {hasResults && (
          <ul className="list-none p-0 m-0">
            {results.map((partner, index) => {
              const name = getDisplayName(partner);
              const distanceLabel = getDistanceLabel(partner);
              return (
                <li
                  key={partner.userId || partner.id || index}
                  className="flex align-items-center p-2 cursor-pointer"
                  onClick={() => onSelect && onSelect(partner)}
                  style={{ gap: "0.75rem" }}
                >
                  <div
                    className="flex align-items-center justify-content-center border-circle"
                    style={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: "var(--theme-border, #eaeaea)",
                      color: "var(--theme-text, #171717)",
                      flexShrink: 0,
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    <UserInitialsAvatar
                      avatarUrl={partner?.user?.urlAvatar}
                      firstName={partner?.user?.firstname}
                      lastName={partner?.user?.surname}
                      fullName={name}
                      fallback="P"
                      style={{ width: "100%", height: "100%", fontSize: "0.9rem" }}
                      alt={name}
                    />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        color: "var(--theme-text, #171717)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {name}
                    </div>
                    {distanceLabel && (
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "var(--theme-text-muted, #5C5C5C)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <i className="pi pi-map-marker" style={{ fontSize: "0.75rem" }} />{" "}
                        {distanceLabel} {safeLocaleText("partnerB2bFromYou", "from you")}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PartnerB2BResultsList;
